import { useCallback, useEffect, useState } from "react";
import { Checkbox, Tooltip } from "@narmi/design_system";

import { entities, modules } from "byzantine";
import User from "byzantine/src/User";
import { useLocalization } from "@fluent/react";
import { useNotificationContext } from "cerulean";
import styles from "./DeliveryChannels.module.scss";
import { useCurrentUser } from "../../../../contexts/CurrentUserContext";
import SmsTransactionalDisclosureDialog from "../../SmsTransactionalDisclosureDialog";

interface DeliveryChannelsProps {
  alertKey: API.AlertKey;
}

const DeliveryChannels = ({ alertKey }: DeliveryChannelsProps) => {
  const { l10n } = useLocalization();
  const { form } = modules.alerts.useUserAlertsForm(alertKey);
  const { currentUser, setCurrentUser } = useCurrentUser();
  const { sendNotification } = useNotificationContext();
  const { acceptTransactionalSmsDisclosure, loading: smsDisclosureLoading } =
    entities.useAcceptTransactionalSmsDisclosure();
  const [showDisclosure, setShowDisclosure] = useState(false);
  const {
    values: { deliveryChannels },
  } = form;

  const isSmsDisabled = !currentUser?.phone?.number;

  useEffect(() => {
    if (
      deliveryChannels.includes("sms") &&
      !currentUser?.has_accepted_transactional_sms
    ) {
      form.setFieldValue(
        "deliveryChannels",
        deliveryChannels.filter((ch) => ch !== "sms")
      );
    }
  }, [deliveryChannels, currentUser]);

  const hasEmail = deliveryChannels.includes("email");
  const hasSMS = deliveryChannels.includes("sms");

  const handleChannelChange = useCallback(
    (channel: API.DeliveryChannel, newHas: boolean) => {
      let newChannels: API.DeliveryChannel[];
      if (newHas) {
        if (!deliveryChannels.includes(channel)) {
          newChannels = [...deliveryChannels, channel];
        } else {
          newChannels = deliveryChannels;
        }
      } else if (deliveryChannels.filter((ch) => ch !== "push").length > 1) {
        newChannels = deliveryChannels.filter((ch) => ch !== channel);
      } else {
        newChannels = deliveryChannels;
      }
      form.setFieldValue("deliveryChannels", newChannels);
    },
    [deliveryChannels, form]
  );

  const emailOnChange = useCallback(() => {
    handleChannelChange("email", !hasEmail);
  }, [handleChannelChange, hasEmail]);

  const smsOnChange = useCallback(() => {
    if (!hasSMS && !currentUser?.has_accepted_transactional_sms) {
      setShowDisclosure(true);
    } else {
      handleChannelChange("sms", !hasSMS);
    }
  }, [handleChannelChange, hasSMS, currentUser]);

  const smsLabel = l10n.getString(
    "alerts-tooltip-label-sms-disabled",
    {},
    "Update your phone number in profile settings to receive SMS alerts."
  );

  return (
    <div className={styles.container}>
      <Checkbox label="Email" checked={hasEmail} onChange={emailOnChange} />
      {isSmsDisabled ? (
        <Tooltip text={smsLabel}>
          <Checkbox
            label="SMS"
            checked={hasSMS}
            onChange={smsOnChange}
            disabled={isSmsDisabled}
          />
        </Tooltip>
      ) : (
        <Checkbox
          label="SMS"
          checked={hasSMS}
          onChange={smsOnChange}
          disabled={isSmsDisabled}
        />
      )}
      <SmsTransactionalDisclosureDialog
        isOpen={showDisclosure}
        onClose={() => setShowDisclosure(false)}
        onAccept={() =>
          acceptTransactionalSmsDisclosure({
            onSuccess: () => {
              if (currentUser) {
                setCurrentUser(
                  new User({
                    ...currentUser,
                    has_accepted_transactional_sms: true,
                  })
                );
              }
              handleChannelChange("sms", true);
              setShowDisclosure(false);
            },
            onError: () => {
              sendNotification({
                type: "negative",
                text: l10n.getString("error-unknown-try-again"),
              });
              setShowDisclosure(false);
            },
          })
        }
        loading={smsDisclosureLoading}
      />
    </div>
  );
};

export default DeliveryChannels;
